/* Merged Correlations + Causality view — one interactive constellation.

   Every workspace signal is a node. Edges carry two kinds of statistical
   relationship:
     · correlation (undirected, green positive / red negative)
     · Granger causality (directed arrow, with lag + p-value)
   A strength knob filters both kinds; nodes light up when they touch a
   visible edge. Clicking an edge or node opens the detail drawer on the
   right — the same interaction grammar as the discovery graph.

   Rendering architecture: nodes stay React Flow components (drag, click,
   fit-view), but edges are drawn on a single <canvas> layer synced to the
   viewport. SVG edge components cap out around ~2k edges; the canvas draws
   tens of thousands per frame, so every relationship can be shown without
   crashing the tab. Edge hover/click works via nearest-segment hit testing
   on the pane.

   Readability at scale comes from BUNDLED mode: signals that co-move
   strongly form families (the same union-find used by the ring layout),
   and cross-family edges collapse into one bundled link with a ×count
   badge. Hovering or selecting a node fans its real edges back out.
   The "All edges" toggle draws every individual edge instead. */

import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ReactFlow, ReactFlowProvider, Background, Controls, Handle, Position,
  useNodesState, useReactFlow, useViewport,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Box, Card, Chip, CircularProgress, Divider, Drawer, IconButton, Slider,
  Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import EastIcon from '@mui/icons-material/East';
import SyncAltIcon from '@mui/icons-material/SyncAlt';

import { fetchRequest as fetchCorrelation } from '../../redux/actions/signals/correlation';
import { fetchRequest as fetchCausality } from '../../redux/actions/signals/causality';
import { useDisplayNames } from '../../utils/displayNames';

/* ── Constants ───────────────────────────────────────────────────────── */

const NODE_R = { lit: 30, dim: 16 };
const DEFAULT_MIN_STRENGTH = 0.5;
const STRENGTH_MARKS = [
  { value: 0, label: 'all' },
  { value: 0.5, label: 'moderate' },
  { value: 0.8, label: 'strong' },
];
// Above this many correlation edges the view defaults to bundled mode —
// individual chords stop being readable long before canvas stops being fast.
const BUNDLE_DEFAULT_ABOVE = 400;
// Cap on hover value-pills: past this count they overlap into noise.
const MAX_HOVER_LABELS = 40;
// System-materialized kernel channels (event decay/step/info-age) — they
// duplicate their event stream's identity, so hide them here.
const KERNEL_KEY_RE = /__(k\d+[hd](_signed)?|step(_signed)?|info_age)$/;

const EMPTY_EDGES = [];

const cssId = (id) => (window.CSS && window.CSS.escape ? window.CSS.escape(id) : id);
const pairKey = (a, b) => (a < b ? `${a}|${b}` : `${b}|${a}`);
const signed = (v, digits = 2) => `${v > 0 ? '+' : ''}${v.toFixed(digits)}`;

/* ── The pulse glyph (same mark as the discovery graph) ──────────────── */

const Pulse = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path
      d="M4 16h6l3-8 5 16 3-8h7"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CircleNode = ({ data }) => {
  const theme = useTheme();
  const accent = data.accent;
  const r = data.radius;

  return (
    <Box sx={{
      width: r * 2,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      opacity: data.related ? 1 : 0.35,
      cursor: 'pointer',
    }}>
      <Box sx={{
        width: r * 2,
        height: r * 2,
        borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: `1.5px solid ${alpha(accent, data.related ? 0.9 : 0.5)}`,
        backgroundColor: alpha(theme.palette.background.default, 0.92),
        boxShadow: data.related
          ? `0 0 ${r / 2}px ${alpha(accent, 0.4)}, inset 0 0 ${r / 2.5}px ${alpha(accent, 0.2)}`
          : 'none',
      }}>
        <Pulse size={r * 1.15} color={accent} />
      </Box>
      <Typography
        noWrap
        sx={{
          mt: 0.75,
          maxWidth: 150,
          px: 0.75, py: 0.1,
          fontSize: 11,
          fontWeight: 500,
          fontFamily: 'monospace',
          color: data.related ? 'text.primary' : 'text.disabled',
          backgroundColor: alpha(theme.palette.background.paper, 0.85),
          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          borderRadius: 0.75,
        }}
      >
        {data.label}
      </Typography>
      {/* Both handles sit at the circle's CENTER; kept (hidden) so React
          Flow treats the node as connectable-looking even though edges are
          canvas-drawn now. */}
      <Handle type="target" position={Position.Top}
        style={{ opacity: 0, top: r, left: r, transform: 'translate(-50%, -50%)' }} />
      <Handle type="source" position={Position.Top}
        style={{ opacity: 0, top: r, left: r, transform: 'translate(-50%, -50%)' }} />
    </Box>
  );
};

const nodeTypes = { circle: CircleNode };

/* Re-fit the camera when the knob changes the visible set. */
const Refit = ({ signal }) => {
  const { fitView } = useReactFlow();
  useEffect(() => {
    const t = setTimeout(
      () => fitView({ padding: 0.1, maxZoom: 1.2, duration: 350 }), 40);
    return () => clearTimeout(t);
  }, [signal, fitView]);
  return null;
};

/* ── Data shaping ────────────────────────────────────────────────────── */

/* Correlation matrix {A: {B: v}} → one edge per unordered pair. */
const buildCorrEdges = (matrix) => {
  const keys = Object.keys(matrix || {}).filter((k) => !KERNEL_KEY_RE.test(k));
  const edges = [];
  keys.forEach((a, i) => {
    keys.slice(i + 1).forEach((b) => {
      const v = matrix?.[a]?.[b];
      if (v == null || Number.isNaN(v)) return;
      const corr = Number(v);
      if (Math.abs(corr) < 0.01) return;
      edges.push({ id: `c|${pairKey(a, b)}`, a, b, corr, strength: Math.abs(corr) });
    });
  });
  return edges;
};

/* causality_network.edges → directed edges keyed by ordered pair. */
const buildCauseEdges = (causality) => {
  const list = causality?.causality_network?.edges || [];
  return list
    .filter((e) => e?.from && e?.to
      && !KERNEL_KEY_RE.test(e.from) && !KERNEL_KEY_RE.test(e.to))
    .map((e) => ({
      id: `g|${e.from}>${e.to}`,
      a: e.from,
      b: e.to,
      strength: Math.abs(Number(e.strength) || 0),
      p_value: e.p_value,
      lag: e.optimal_lag,
    }));
};

/* Clustered ring layout. Force-directed layouts turn dense correlation
   graphs into hairballs; a circle is deterministic and always readable —
   nodes on the rim, relationships as chords. Signals that co-move
   strongly (|corr| ≥ CLUSTER_CORR) are grouped into families and placed
   ADJACENT on the ring (with a small gap between families), so the
   strongest edges become short arcs along the rim and only genuine
   cross-family relationships cross the middle. The same families feed
   the bundled edge view. */
const CLUSTER_CORR = 0.75;
const RING_SPACING = 130;   // arc length reserved per node

const computeLayout = (nodeIds, corrEdges, causeEdges) => {
  const n = nodeIds.length;
  if (!n) return { positions: {}, radius: 260, families: [] };

  // Union-find families over strong co-movement.
  const parent = {};
  nodeIds.forEach((id) => { parent[id] = id; });
  const find = (x) => {
    let r = x;
    while (parent[r] !== r) { parent[r] = parent[parent[r]]; r = parent[r]; }
    return r;
  };
  corrEdges
    .filter((e) => e.strength >= CLUSTER_CORR)
    .forEach((e) => { parent[find(e.a)] = find(e.b); });

  // Total connection weight per node — hubs go first within a family.
  const weight = {};
  [...corrEdges, ...causeEdges].forEach((e) => {
    weight[e.a] = (weight[e.a] || 0) + e.strength;
    weight[e.b] = (weight[e.b] || 0) + e.strength;
  });

  const clusters = {};
  nodeIds.forEach((id) => {
    const root = find(id);
    (clusters[root] = clusters[root] || []).push(id);
  });
  const clusterList = Object.values(clusters)
    .map((members) => members.sort((a, b) => (weight[b] || 0) - (weight[a] || 0)))
    .sort((x, y) => y.length - x.length);

  // An empty slot AFTER each multi-member family keeps families visually
  // separable; singletons pack tight (a gap after each one would just
  // bloat the ring).
  const gaps = clusterList.length > 1
    ? clusterList.filter((c) => c.length > 1).length
    : 0;
  const slots = n + gaps;
  const radius = Math.max(260, (slots * RING_SPACING) / (2 * Math.PI));

  const positions = {};
  let slot = 0;
  clusterList.forEach((members) => {
    members.forEach((id) => {
      const angle = (2 * Math.PI * slot) / slots - Math.PI / 2;
      positions[id] = {
        x: radius * Math.cos(angle),
        y: radius * Math.sin(angle),
      };
      slot += 1;
    });
    if (gaps && members.length > 1) slot += 1;
  });
  return { positions, radius, families: clusterList };
};

/* ── Canvas edge layer ───────────────────────────────────────────────── */

/* Point-to-segment distance, for edge hit testing in graph coordinates. */
const distToSeg = (px, py, x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const l2 = dx * dx + dy * dy;
  if (!l2) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / l2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
};

const drawArrow = (ctx, x1, y1, x2, y2, size, fill) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - ux * size - uy * size * 0.45, y2 - uy * size + ux * size * 0.45);
  ctx.lineTo(x2 - ux * size + uy * size * 0.45, y2 - uy * size - ux * size * 0.45);
  ctx.closePath();
  ctx.fill();
};

const edgeLabelText = (e) => {
  if (e.kind === 'corr') return signed(e.corr);
  if (e.kind === 'cause') return e.lag != null ? `lag ${e.lag}` : e.strength.toFixed(2);
  if (e.kind === 'bcorr') return `×${e.count} · avg ${signed(e.meanCorr)}`;
  return `×${e.count} causal`;
};

/* All edges — individual, bundled, and the hovered node's fan-out — are
   drawn here in one pass. Strokes are batched by style bucket so even
   10k+ edges cost a handful of path/stroke calls per frame. Everything
   is drawn in screen space (endpoints transformed manually), so lines
   stay crisp at every zoom and label pills keep a constant size. */
const EdgeCanvas = ({ geom, hoveredEdgeId, activeNodeId }) => {
  const theme = useTheme();
  const { x: vx, y: vy, zoom } = useViewport();
  const canvasRef = useRef(null);
  const dashRef = useRef(0);
  const drawRef = useRef(() => {});

  drawRef.current = () => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cvs.width / dpr, cvs.height / dpr);

    const X = (v) => v * zoom + vx;
    const Y = (v) => v * zoom + vy;
    const pos = theme.palette.success.main;
    const neg = theme.palette.error.main;
    const inf = theme.palette.info.main;

    const isLit = (e) => !activeNodeId || e.a === activeNodeId || e.b === activeNodeId;

    const pill = (cx, cy, text, color) => {
      ctx.font = '10px ui-monospace, SFMono-Regular, Menlo, monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const w = ctx.measureText(text).width + 10;
      const h = 16;
      ctx.fillStyle = alpha(theme.palette.background.paper, 0.92);
      ctx.strokeStyle = alpha(theme.palette.divider, 0.8);
      ctx.lineWidth = 1;
      ctx.setLineDash([]);
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(cx - w / 2, cy - h / 2, w, h, 6);
      else ctx.rect(cx - w / 2, cy - h / 2, w, h);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = color;
      ctx.fillText(text, cx, cy);
    };

    const edgeColor = (e) => {
      if (e.kind === 'cause' || e.kind === 'bcause') return inf;
      const v = e.kind === 'bcorr' ? e.meanCorr : e.corr;
      return v >= 0 ? pos : neg;
    };

    /* 1 · Bundled links (under individual edges). */
    const bundleDim = activeNodeId ? 0.15 : 1;
    geom.bundles.forEach((b) => {
      const color = edgeColor(b);
      ctx.strokeStyle = alpha(color, 0.55 * bundleDim);
      ctx.lineWidth = Math.min(7, 1.5 + Math.log2(b.count));
      ctx.lineCap = 'round';
      ctx.setLineDash(b.kind === 'bcause' ? [6, 4] : []);
      ctx.lineDashOffset = -dashRef.current;
      ctx.beginPath();
      ctx.moveTo(X(b.x1), Y(b.y1));
      ctx.lineTo(X(b.x2), Y(b.y2));
      ctx.stroke();
      if (b.kind === 'bcause') {
        drawArrow(ctx, X(b.x1), Y(b.y1), X(b.x2), Y(b.y2), 10,
          alpha(inf, 0.9 * bundleDim));
      }
    });
    ctx.setLineDash([]);

    /* 2 · Individual edges + fan-out, batched by style bucket. */
    const groups = new Map();
    const queue = (e, forceLit) => {
      const lit = forceLit || isLit(e);
      const isCause = e.kind === 'cause';
      const color = edgeColor(e);
      const a = (isCause ? 0.7 : 0.45) * (lit ? 1 : 0.08);
      const wFlow = isCause
        ? Math.max(1.25, Math.min(3.5, e.strength * 3.5))
        : Math.max(1, Math.min(4, e.strength * 4));
      const w = Math.max(0.35, Math.round(wFlow * zoom * 4) / 4);
      const key = `${isCause ? 1 : 0}|${color}|${a.toFixed(3)}|${w}`;
      let g = groups.get(key);
      if (!g) {
        g = { color, alpha: a, width: w, dash: isCause, segs: [] };
        groups.set(key, g);
      }
      g.segs.push(e);
    };
    geom.individuals.forEach((e) => queue(e, false));
    geom.fan.forEach((e) => queue(e, true));

    groups.forEach((g) => {
      ctx.strokeStyle = alpha(g.color, g.alpha);
      ctx.lineWidth = g.width;
      ctx.lineCap = 'round';
      ctx.setLineDash(g.dash ? [6 * zoom, 4 * zoom] : []);
      ctx.lineDashOffset = g.dash ? -dashRef.current * zoom : 0;
      ctx.beginPath();
      g.segs.forEach((e) => {
        ctx.moveTo(X(e.x1), Y(e.y1));
        ctx.lineTo(X(e.x2), Y(e.y2));
      });
      ctx.stroke();
    });
    ctx.setLineDash([]);

    const litArrow = alpha(inf, 0.9);
    const dimArrow = alpha(inf, 0.08);
    geom.individuals.forEach((e) => {
      if (e.kind !== 'cause') return;
      drawArrow(ctx, X(e.x1), Y(e.y1), X(e.x2), Y(e.y2),
        Math.max(4, 13 * zoom), isLit(e) ? litArrow : dimArrow);
    });
    geom.fan.forEach((e) => {
      if (e.kind !== 'cause') return;
      drawArrow(ctx, X(e.x1), Y(e.y1), X(e.x2), Y(e.y2),
        Math.max(4, 13 * zoom), litArrow);
    });

    /* 3 · Hover highlight — thicker restroke of the hovered edge/bundle. */
    let hovered = null;
    if (hoveredEdgeId) {
      hovered = geom.bundles.find((e) => e.id === hoveredEdgeId)
        || geom.individuals.find((e) => e.id === hoveredEdgeId)
        || geom.fan.find((e) => e.id === hoveredEdgeId);
    }
    if (hovered) {
      const color = edgeColor(hovered);
      const isBundle = hovered.kind === 'bcorr' || hovered.kind === 'bcause';
      ctx.strokeStyle = alpha(color, 0.95);
      ctx.lineWidth = isBundle
        ? Math.min(8, 2.5 + Math.log2(hovered.count))
        : Math.max(1.5, Math.min(4, hovered.strength * 4) * zoom + 1);
      ctx.lineCap = 'round';
      const dashed = hovered.kind === 'cause' || hovered.kind === 'bcause';
      ctx.setLineDash(dashed ? [6 * (isBundle ? 1 : zoom), 4 * (isBundle ? 1 : zoom)] : []);
      ctx.beginPath();
      ctx.moveTo(X(hovered.x1), Y(hovered.y1));
      ctx.lineTo(X(hovered.x2), Y(hovered.y2));
      ctx.stroke();
      ctx.setLineDash([]);
    }

    /* 4 · Label pills (screen-constant size, so they read at any zoom). */
    if (!activeNodeId) {
      geom.bundles.forEach((b) => {
        pill((X(b.x1) + X(b.x2)) / 2, (Y(b.y1) + Y(b.y2)) / 2,
          `×${b.count}`, edgeColor(b));
      });
    }
    if (activeNodeId) {
      const touching = [
        ...geom.individuals.filter((e) => e.a === activeNodeId || e.b === activeNodeId),
        ...geom.fan,
      ];
      if (touching.length <= MAX_HOVER_LABELS) {
        touching.forEach((e) => {
          pill((X(e.x1) + X(e.x2)) / 2, (Y(e.y1) + Y(e.y2)) / 2,
            edgeLabelText(e), edgeColor(e));
        });
      }
    }
    if (hovered) {
      pill((X(hovered.x1) + X(hovered.x2)) / 2, (Y(hovered.y1) + Y(hovered.y2)) / 2,
        edgeLabelText(hovered), edgeColor(hovered));
    }
  };

  // Keep the canvas bitmap matched to its container and pixel ratio.
  useEffect(() => {
    const cvs = canvasRef.current;
    const host = cvs?.parentElement;
    if (!cvs || !host) return undefined;
    const fit = () => {
      const dpr = window.devicePixelRatio || 1;
      cvs.width = host.clientWidth * dpr;
      cvs.height = host.clientHeight * dpr;
      cvs.style.width = `${host.clientWidth}px`;
      cvs.style.height = `${host.clientHeight}px`;
      drawRef.current();
    };
    const ro = new ResizeObserver(fit);
    ro.observe(host);
    fit();
    return () => ro.disconnect();
  }, []);

  // Redraw after every render — renders are triggered exactly by the
  // things that change the picture (viewport, geometry, hover state).
  useEffect(() => { drawRef.current(); });

  // Marching-ants animation for causal edges, only while any exist.
  useEffect(() => {
    if (!geom.hasCause) return undefined;
    let raf;
    const loop = (ts) => {
      dashRef.current = (ts / 40) % 10;
      drawRef.current();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [geom.hasCause]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
      }}
    />
  );
};

/* ── Graph surface: canvas + React Flow + pane hit testing ───────────── */

const GraphSurface = ({
  nodes, onNodesChange, display, fanOut, families, activeNodeId,
  minZoom, refitSignal, onOpenFocus, onNodeClick, onNodeEnter, onNodeLeave,
}) => {
  const theme = useTheme();
  const { screenToFlowPosition, getZoom } = useReactFlow();
  const [hoveredEdgeId, setHoveredEdgeId] = useState(null);

  // Live node centers (follow drags), keyed by signal id.
  const centers = useMemo(() => {
    const m = {};
    nodes.forEach((n) => {
      const r = n.data.radius;
      m[n.id] = { x: n.position.x + r, y: n.position.y + r, r };
    });
    return m;
  }, [nodes]);

  // Family anchor = centroid of member centers; bundles run between them.
  const anchors = useMemo(() => families.map((members) => {
    let sx = 0;
    let sy = 0;
    let c = 0;
    members.forEach((id) => {
      const p = centers[id];
      if (p) { sx += p.x; sy += p.y; c += 1; }
    });
    return c ? { x: sx / c, y: sy / c } : null;
  }), [families, centers]);

  // Positioned edge geometry in graph coordinates.
  const geom = useMemo(() => {
    const seg = (a, b, causal) => {
      const ca = centers[a];
      const cb = centers[b];
      if (!ca || !cb) return null;
      const dx = cb.x - ca.x;
      const dy = cb.y - ca.y;
      const len = Math.hypot(dx, dy) || 1;
      const rs = ca.r + 4;
      const rt = cb.r + (causal ? 10 : 4);
      if (len <= rs + rt) return null;
      const ux = dx / len;
      const uy = dy / len;
      return {
        x1: ca.x + ux * rs,
        y1: ca.y + uy * rs,
        x2: cb.x - ux * rt,
        y2: cb.y - uy * rt,
      };
    };

    const individuals = [];
    display.indiv.forEach((e) => {
      const s = seg(e.a, e.b, e.kind === 'cause');
      if (s) individuals.push({ ...e, ...s });
    });

    const bundles = [];
    display.bundles.forEach((b) => {
      const pa = anchors[b.fa];
      const pb = anchors[b.fb];
      if (!pa || !pb) return;
      const dx = pb.x - pa.x;
      const dy = pb.y - pa.y;
      const len = Math.hypot(dx, dy) || 1;
      const trim = Math.min(46, len / 3);
      const ux = dx / len;
      const uy = dy / len;
      bundles.push({
        ...b,
        x1: pa.x + ux * trim,
        y1: pa.y + uy * trim,
        x2: pb.x - ux * trim,
        y2: pb.y - uy * trim,
      });
    });

    const fan = [];
    fanOut.forEach((e) => {
      const s = seg(e.a, e.b, e.kind === 'cause');
      if (s) fan.push({ ...e, ...s });
    });

    const hasCause = individuals.some((e) => e.kind === 'cause')
      || fan.some((e) => e.kind === 'cause')
      || bundles.some((b) => b.kind === 'bcause');

    return { individuals, bundles, fan, hasCause };
  }, [display, fanOut, centers, anchors]);

  const hitTest = useCallback((fx, fy, zoom) => {
    const th = Math.max(6 / zoom, 3);
    let best = null;
    let bestD = th;
    [...geom.individuals, ...geom.fan].forEach((e) => {
      const d = distToSeg(fx, fy, e.x1, e.y1, e.x2, e.y2);
      if (d < bestD) { bestD = d; best = e; }
    });
    if (best) return best;
    bestD = Math.max(10 / zoom, 5);
    geom.bundles.forEach((b) => {
      const d = distToSeg(fx, fy, b.x1, b.y1, b.x2, b.y2);
      if (d < bestD) { bestD = d; best = b; }
    });
    return best;
  }, [geom]);

  const handlePaneMouseMove = useCallback((event) => {
    const p = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    const hit = hitTest(p.x, p.y, getZoom());
    const id = hit ? hit.id : null;
    setHoveredEdgeId((prev) => (prev === id ? prev : id));
  }, [screenToFlowPosition, getZoom, hitTest]);

  const handlePaneClick = useCallback((event) => {
    const p = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    const hit = hitTest(p.x, p.y, getZoom());
    if (!hit) return;
    if (hit.kind === 'bcorr' || hit.kind === 'bcause') {
      onOpenFocus({ type: 'bundle', id: hit.id });
    } else {
      onOpenFocus({ type: 'pair', a: hit.a, b: hit.b });
    }
  }, [screenToFlowPosition, getZoom, hitTest, onOpenFocus]);

  return (
    <>
      {hoveredEdgeId && (
        <style>{'.sig-flow .react-flow__pane { cursor: pointer; }'}</style>
      )}
      <EdgeCanvas
        geom={geom}
        hoveredEdgeId={hoveredEdgeId}
        activeNodeId={activeNodeId}
      />
      <ReactFlow
        nodes={nodes}
        edges={EMPTY_EDGES}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        fitView
        fitViewOptions={{ padding: 0.1, maxZoom: 1.2 }}
        minZoom={minZoom}
        proOptions={{ hideAttribution: true }}
        onNodeClick={(_e, node) => onNodeClick(node.id)}
        onNodeMouseEnter={(_e, node) => onNodeEnter(node.id)}
        onNodeMouseLeave={onNodeLeave}
        onPaneMouseMove={handlePaneMouseMove}
        onPaneMouseLeave={() => setHoveredEdgeId(null)}
        onPaneClick={handlePaneClick}
        nodesConnectable={false}
        elementsSelectable
        // Transparent so the canvas edge layer beneath stays visible.
        style={{ position: 'relative', zIndex: 1, '--xy-background-color': 'transparent' }}
      >
        <Background gap={26} size={1.2} color={alpha(theme.palette.text.primary, 0.07)} />
        <Controls showInteractive={false} />
        <Refit signal={refitSignal} />
      </ReactFlow>
    </>
  );
};

/* ── Detail drawer ───────────────────────────────────────────────────── */

const StatRow = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" sx={{ py: 0.25 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{value}</Typography>
  </Stack>
);

const CorrChip = ({ value, theme }) => (
  <Chip
    size="small"
    label={`${value > 0 ? '+' : ''}${value.toFixed(3)}`}
    sx={{
      fontFamily: 'monospace',
      fontWeight: 700,
      backgroundColor: alpha(
        value >= 0 ? theme.palette.success.main : theme.palette.error.main, 0.15),
      color: value >= 0 ? theme.palette.success.main : theme.palette.error.main,
    }}
  />
);

const CauseCard = ({ from, to, info, theme, nameOf = (k) => k }) => (
  <Box sx={{
    p: 1.25, borderRadius: 1,
    border: `1px solid ${alpha(theme.palette.info.main, 0.4)}`,
    backgroundColor: alpha(theme.palette.info.main, 0.06),
  }}>
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700 }} title={from}>
        {nameOf(from)}
      </Typography>
      <EastIcon sx={{ fontSize: 14, color: 'info.main' }} />
      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700 }} title={to}>
        {nameOf(to)}
      </Typography>
    </Stack>
    <StatRow label="Causal strength" value={Number(info.strength ?? 0).toFixed(3)} />
    {info.p_value != null && <StatRow label="P-value" value={Number(info.p_value).toFixed(4)} />}
    {info.lag != null && <StatRow label="Optimal lag" value={`${info.lag}`} />}
    {info.f_statistic != null && <StatRow label="F-statistic" value={Number(info.f_statistic).toFixed(2)} />}
  </Box>
);

const DetailDrawer = ({
  focus, onClose, onFocusPair, corrEdges, causeEdges, bundles, families,
  theme, nameOf = (k) => k,
}) => {
  const content = useMemo(() => {
    if (!focus) return null;

    if (focus.type === 'pair') {
      const { a, b } = focus;
      const corr = corrEdges.find((e) => e.id === `c|${pairKey(a, b)}`);
      const causes = causeEdges.filter(
        (e) => (e.a === a && e.b === b) || (e.a === b && e.b === a));
      return (
        <>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }} title={a}>{nameOf(a)}</Typography>
            <SyncAltIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }} title={b}>{nameOf(b)}</Typography>
          </Stack>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">Correlation</Typography>
            {corr
              ? <CorrChip value={corr.corr} theme={theme} />
              : <Typography variant="caption" color="text.disabled">below floor</Typography>}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Causality {causes.length === 0 && (
              <Typography component="span" variant="caption" color="text.disabled">
                — none significant
              </Typography>
            )}
          </Typography>
          <Stack spacing={1}>
            {causes.map((c) => (
              <CauseCard key={c.id} from={c.a} to={c.b} info={c} theme={theme} nameOf={nameOf} />
            ))}
          </Stack>
        </>
      );
    }

    if (focus.type === 'bundle') {
      const bundle = bundles.find((b) => b.id === focus.id);
      if (!bundle) {
        return (
          <Typography variant="caption" color="text.disabled">
            This bundle is no longer visible at the current strength filter.
          </Typography>
        );
      }
      const labelOf = (fi) => {
        const members = families[fi] || [];
        if (!members.length) return '?';
        return `${nameOf(members[0])}${members.length > 1 ? ` +${members.length - 1}` : ''}`;
      };
      const isCause = bundle.kind === 'bcause';
      const rows = [...bundle.edges].sort((x, y) => y.strength - x.strength);
      return (
        <>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
              {labelOf(bundle.fa)}
            </Typography>
            {isCause
              ? <EastIcon sx={{ fontSize: 16, color: 'info.main' }} />
              : <SyncAltIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
            <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
              {labelOf(bundle.fb)}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {bundle.count} {isCause ? 'causal links' : 'correlations'} between
            these signal groups
            {!isCause && ` · avg ${signed(bundle.meanCorr)}`}
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Stack spacing={0.75}>
            {rows.map((e) => (
              <Box
                key={e.id}
                onClick={() => onFocusPair(e.a, e.b)}
                sx={{
                  p: 1, borderRadius: 1, cursor: 'pointer',
                  border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography noWrap variant="caption" sx={{ fontFamily: 'monospace' }}>
                    {nameOf(e.a)} {isCause ? '→' : '↔'} {nameOf(e.b)}
                  </Typography>
                  {isCause
                    ? (
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }} color="info.main">
                        {e.lag != null ? `lag ${e.lag}` : Number(e.strength).toFixed(2)}
                      </Typography>
                    )
                    : <CorrChip value={e.corr} theme={theme} />}
                </Stack>
              </Box>
            ))}
          </Stack>
        </>
      );
    }

    // Node focus: every connection this signal has, strongest first.
    const id = focus.id;
    const rows = {};
    corrEdges.forEach((e) => {
      if (e.a !== id && e.b !== id) return;
      const other = e.a === id ? e.b : e.a;
      rows[other] = { other, corr: e.corr, strength: e.strength, causes: [] };
    });
    causeEdges.forEach((e) => {
      if (e.a !== id && e.b !== id) return;
      const other = e.a === id ? e.b : e.a;
      rows[other] = rows[other] || { other, corr: null, strength: 0, causes: [] };
      rows[other].causes.push(e);
      rows[other].strength = Math.max(rows[other].strength, e.strength);
    });
    const sorted = Object.values(rows).sort((x, y) => y.strength - x.strength);

    return (
      <>
        <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }} title={id}>{nameOf(id)}</Typography>
        <Typography variant="caption" color="text.secondary">
          {sorted.length} connection{sorted.length === 1 ? '' : 's'}
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Stack spacing={0.75}>
          {sorted.map((r) => (
            <Box
              key={r.other}
              onClick={() => onFocusPair(id, r.other)}
              sx={{
                p: 1, borderRadius: 1, cursor: 'pointer',
                border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                  <Typography noWrap variant="caption" sx={{ fontFamily: 'monospace' }} title={r.other}>
                    {nameOf(r.other)}
                  </Typography>
                  {r.causes.map((c) => (
                    <Tooltip key={c.id} title={`${nameOf(c.a)} → ${nameOf(c.b)} · lag ${c.lag ?? '?'}`} arrow>
                      <EastIcon sx={{
                        fontSize: 13, color: 'info.main',
                        transform: c.b === id ? 'none' : 'rotate(180deg)',
                      }} />
                    </Tooltip>
                  ))}
                </Stack>
                {r.corr != null && <CorrChip value={r.corr} theme={theme} />}
              </Stack>
            </Box>
          ))}
          {sorted.length === 0 && (
            <Typography variant="caption" color="text.disabled">
              No relationships above the noise floor.
            </Typography>
          )}
        </Stack>
      </>
    );
  }, [focus, corrEdges, causeEdges, bundles, families, onFocusPair, theme, nameOf]);

  return (
    <Drawer
      anchor="right"
      open={Boolean(focus)}
      onClose={onClose}
      PaperProps={{ sx: { width: 360, maxWidth: '92vw', p: 2 } }}
    >
      <Stack direction="row" justifyContent="flex-end">
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Stack>
      {content}
    </Drawer>
  );
};

/* ── Main component ──────────────────────────────────────────────────── */

const SignalRelationshipsGraph = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  // Friendly display names for raw signal keys ("o" -> "Open").
  const nameOf = useDisplayNames();

  const corrResponse = useSelector((s) => s.signal_correlation?.response);
  const corrLoading = useSelector((s) => Boolean(s.signal_correlation?.isLoading));
  const causResponse = useSelector((s) => s.signal_causality?.response);
  const causLoading = useSelector((s) => Boolean(s.signal_causality?.isLoading));

  const [minStrength, setMinStrength] = useState(DEFAULT_MIN_STRENGTH);
  const [hoveredId, setHoveredId] = useState(null);
  const [focus, setFocus] = useState(null);
  // null until data arrives, then defaults by density; user toggle wins.
  const [mode, setMode] = useState(null);

  const load = () => {
    dispatch(fetchCorrelation());
    // Causality runs with the platform defaults — no parameter form, the
    // knob + drawer carry the exploration instead.
    dispatch(fetchCausality({}));
  };
  useEffect(load, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const matrix = corrResponse?.correlation;
  const causality = causResponse?.causality_results;

  const corrEdges = useMemo(() => buildCorrEdges(matrix), [matrix]);
  const causeEdges = useMemo(() => buildCauseEdges(causality), [causality]);

  useEffect(() => {
    if (mode === null && corrEdges.length) {
      setMode(corrEdges.length > BUNDLE_DEFAULT_ABOVE ? 'bundled' : 'all');
    }
  }, [corrEdges, mode]);
  const effMode = mode || 'all';

  const nodeIds = useMemo(() => {
    const ids = new Set();
    Object.keys(matrix || {})
      .filter((k) => !KERNEL_KEY_RE.test(k))
      .forEach((k) => ids.add(k));
    causeEdges.forEach((e) => { ids.add(e.a); ids.add(e.b); });
    return [...ids].sort();
  }, [matrix, causeEdges]);

  const layout = useMemo(
    () => computeLayout(nodeIds, corrEdges, causeEdges),
    [nodeIds, corrEdges, causeEdges],
  );
  const { positions, radius, families } = layout;

  const familyOf = useMemo(() => {
    const m = {};
    families.forEach((members, i) => members.forEach((id) => { m[id] = i; }));
    return m;
  }, [families]);

  // The ring grows with node count; let the camera pull back far enough
  // to always frame it (fitView is clamped by minZoom).
  const minZoom = useMemo(() => Math.min(0.2, 260 / (radius + 150)), [radius]);

  // Knob filter — both kinds share the 0..1 strength scale.
  const visCorr = useMemo(
    () => corrEdges.filter((e) => e.strength >= minStrength),
    [corrEdges, minStrength],
  );
  const visCause = useMemo(
    () => causeEdges.filter((e) => e.strength >= minStrength),
    [causeEdges, minStrength],
  );
  const litIds = useMemo(() => {
    const lit = new Set();
    visCorr.forEach((e) => { lit.add(e.a); lit.add(e.b); });
    visCause.forEach((e) => { lit.add(e.a); lit.add(e.b); });
    return lit;
  }, [visCorr, visCause]);

  /* Bundling: intra-family edges stay individual (they're short rim arcs);
     cross-family edges collapse into one link per family pair. Count-1
     groups flatten back to the plain edge, so sparse workspaces look
     identical in either mode. */
  const display = useMemo(() => {
    const tag = (list, kind) => list.map((e) => ({ ...e, kind }));
    if (effMode !== 'bundled') {
      return { indiv: [...tag(visCorr, 'corr'), ...tag(visCause, 'cause')], bundles: [] };
    }
    const indiv = [];
    const bundles = [];
    const groupBy = (list, kind, keyFn) => {
      const groups = new Map();
      list.forEach((e) => {
        const fa = familyOf[e.a];
        const fb = familyOf[e.b];
        if (fa == null || fb == null || fa === fb) {
          indiv.push({ ...e, kind });
          return;
        }
        const key = keyFn(fa, fb);
        let arr = groups.get(key);
        if (!arr) { arr = []; groups.set(key, arr); }
        arr.push(e);
      });
      return groups;
    };
    groupBy(visCorr, 'corr', (fa, fb) => (fa < fb ? `${fa}|${fb}` : `${fb}|${fa}`))
      .forEach((edges, key) => {
        if (edges.length === 1) { indiv.push({ ...edges[0], kind: 'corr' }); return; }
        const [fa, fb] = key.split('|').map(Number);
        bundles.push({
          id: `bc|${key}`,
          kind: 'bcorr',
          fa,
          fb,
          edges,
          count: edges.length,
          meanCorr: edges.reduce((s, e) => s + e.corr, 0) / edges.length,
          strength: edges.reduce((s, e) => Math.max(s, e.strength), 0),
        });
      });
    groupBy(visCause, 'cause', (fa, fb) => `${fa}>${fb}`)
      .forEach((edges, key) => {
        if (edges.length === 1) { indiv.push({ ...edges[0], kind: 'cause' }); return; }
        const [fa, fb] = key.split('>').map(Number);
        bundles.push({
          id: `bg|${key}`,
          kind: 'bcause',
          fa,
          fb,
          edges,
          count: edges.length,
          strength: edges.reduce((s, e) => Math.max(s, e.strength), 0),
        });
      });
    return { indiv, bundles };
  }, [effMode, visCorr, visCause, familyOf]);

  // Hovering or selecting a node fans its bundled edges back out.
  const activeNodeId = hoveredId || (focus?.type === 'node' ? focus.id : null);
  const fanOut = useMemo(() => {
    if (!activeNodeId || effMode !== 'bundled') return EMPTY_EDGES;
    const shown = new Set();
    display.indiv.forEach((e) => {
      if (e.a === activeNodeId || e.b === activeNodeId) shown.add(e.id);
    });
    const out = [];
    visCorr.forEach((e) => {
      if ((e.a === activeNodeId || e.b === activeNodeId) && !shown.has(e.id)) {
        out.push({ ...e, kind: 'corr' });
      }
    });
    visCause.forEach((e) => {
      if ((e.a === activeNodeId || e.b === activeNodeId) && !shown.has(e.id)) {
        out.push({ ...e, kind: 'cause' });
      }
    });
    return out;
  }, [activeNodeId, effMode, display, visCorr, visCause]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  useEffect(() => {
    setNodes(nodeIds.map((id) => {
      const related = litIds.has(id);
      const r = related ? NODE_R.lit : NODE_R.dim;
      return {
        id,
        type: 'circle',
        position: {
          x: (positions[id]?.x || 0) - r,
          y: (positions[id]?.y || 0) - r,
        },
        data: {
          label: nameOf(id),
          related,
          radius: r,
          accent: related ? theme.palette.info.main : theme.palette.text.disabled,
        },
        draggable: true,
      };
    }));
  }, [nodeIds, positions, litIds, theme, setNodes, nameOf]);

  // Hover lighting for NODES — pure CSS over stable elements; the edges'
  // hover dimming happens inside the canvas redraw.
  const hoverCss = useMemo(() => {
    if (!hoveredId) return '';
    const litNodes = new Set([hoveredId]);
    [...visCorr, ...visCause].forEach((e) => {
      if (e.a === hoveredId || e.b === hoveredId) {
        litNodes.add(e.a);
        litNodes.add(e.b);
      }
    });
    const nodeSel = [...litNodes].map(
      (id) => `.sig-flow .react-flow__node[data-id="${cssId(id)}"]`).join(',\n');
    return `
      .sig-flow .react-flow__node { opacity: 0.15; }
      ${nodeSel} { opacity: 1; }
    `;
  }, [hoveredId, visCorr, visCause]);

  if (!corrLoading && nodeIds.length === 0) {
    // The backend tells us WHY the matrix is empty — a workspace with
    // signals but unusable timestamps reads very differently from one
    // with no data at all.
    const insufficient = corrResponse?.insufficient;
    const REASON_COPY = {
      need_two_signals: 'Relationships need at least two signals — this '
        + 'workspace only has one. Import another data column to compare against.',
      no_time_spread: 'All data points share a single timestamp, so there is '
        + 'no history to relate. Check that your import mapped a proper '
        + 'date/time column (hours, days, …) — not just one date.',
      no_overlap: 'Your signals never overlap in time, so they can\u2019t be '
        + 'compared. Import data covering a shared period.',
      insufficient_points: 'There isn\u2019t enough overlapping history to '
        + 'compute reliable relationships yet. Keep ingesting data and this '
        + 'view will populate automatically.',
    };
    const reasonText = insufficient && REASON_COPY[insufficient.reason];
    return (
      <Card variant="outlined">
        <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}>
          <Typography variant="subtitle2">
            {reasonText ? 'Not enough data for relationships' : 'No signals to relate yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 420 }}>
            {reasonText || (
              'Import data first — every numeric column becomes a signal, and this '
              + 'view maps how they correlate and which ones lead the others.'
            )}
          </Typography>
        </Stack>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ position: 'relative' }}>
      {hoverCss && <style>{hoverCss}</style>}
      <Box
        className="sig-flow"
        sx={{
          height: 640,
          position: 'relative',
          backgroundColor: alpha(theme.palette.background.default, 0.6),
          '& .react-flow__node': {
            transition: 'opacity 150ms ease',
          },
          '& .react-flow__controls': {
            boxShadow: 'none',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
          },
          '& .react-flow__controls-button': {
            backgroundColor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`,
            '& svg': { fill: theme.palette.text.secondary },
            '&:hover': { backgroundColor: theme.palette.action.hover },
          },
        }}
      >
        <ReactFlowProvider>
          <GraphSurface
            nodes={nodes}
            onNodesChange={onNodesChange}
            display={display}
            fanOut={fanOut}
            families={families}
            activeNodeId={activeNodeId}
            minZoom={minZoom}
            refitSignal={minStrength}
            onOpenFocus={setFocus}
            onNodeClick={(id) => setFocus({ type: 'node', id })}
            onNodeEnter={setHoveredId}
            onNodeLeave={() => setHoveredId(null)}
          />
        </ReactFlowProvider>

        {corrLoading && (
          <Stack
            alignItems="center" justifyContent="center"
            sx={{
              position: 'absolute', inset: 0, zIndex: 2,
              backgroundColor: alpha(theme.palette.background.default, 0.6),
            }}
          >
            <CircularProgress size={28} />
          </Stack>
        )}
      </Box>

      {/* Header chrome */}
      <Stack
        direction="row" spacing={1.25} alignItems="center"
        sx={{ position: 'absolute', top: 10, left: 12 }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {litIds.size} of {nodeIds.length} signals connected ·{' '}
          {visCorr.length} correlation{visCorr.length === 1 ? '' : 's'} ·{' '}
          {visCause.length} causal
          {effMode === 'bundled' && display.bundles.length > 0
            && ` · ${display.bundles.length} bundles`}
        </Typography>
        {causLoading && (
          <Chip
            size="small"
            icon={<CircularProgress size={10} sx={{ ml: 0.5 }} />}
            label="running causality…"
            sx={{ height: 20, fontSize: 10 }}
          />
        )}
      </Stack>

      {/* Legend */}
      <Stack
        direction="row" spacing={1.5}
        sx={{ position: 'absolute', bottom: 10, right: 12 }}
      >
        {[
          { color: theme.palette.success.main, label: '+ corr' },
          { color: theme.palette.error.main, label: '− corr' },
          { color: theme.palette.info.main, label: 'causal →' },
          ...(effMode === 'bundled'
            ? [{ color: theme.palette.text.secondary, label: 'bundle ×n', thick: true }]
            : []),
        ].map((l) => (
          <Stack key={l.label} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{
              width: 14, height: l.thick ? 4 : 2,
              backgroundColor: l.color, borderRadius: 1,
            }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
              {l.label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      {/* Edge display mode — bundled families vs every individual edge. */}
      <Stack
        spacing={0.25}
        sx={{
          position: 'absolute', top: 8, right: 260,
          px: 1.25, pt: 0.75, pb: 0.75,
          borderRadius: 1,
          border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
          backgroundColor: alpha(theme.palette.background.paper, 0.85),
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          Edges
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={effMode}
          onChange={(_e, v) => v && setMode(v)}
          sx={{
            '& .MuiToggleButton-root': {
              px: 1, py: 0.1, fontSize: 10, textTransform: 'none', lineHeight: 1.6,
            },
          }}
        >
          <ToggleButton value="bundled">Bundled</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Strength knob — shared scale for |corr| and causal strength. */}
      <Stack
        spacing={0}
        sx={{
          position: 'absolute', top: 8, right: 48,
          width: 200, px: 2, pt: 0.75, pb: 2,
          borderRadius: 1,
          border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
          backgroundColor: alpha(theme.palette.background.paper, 0.85),
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, mb: 0.25 }}>
          Min strength · {minStrength.toFixed(2)}
        </Typography>
        <Slider
          size="small"
          value={minStrength}
          onChange={(_e, v) => setMinStrength(Array.isArray(v) ? v[0] : v)}
          min={0}
          max={0.95}
          step={0.05}
          marks={STRENGTH_MARKS}
          sx={{
            py: 0.5,
            '& .MuiSlider-markLabel': {
              fontSize: 9,
              top: 16,
              color: 'text.secondary',
            },
            // Keep the edge labels inside the box instead of bleeding out.
            '& .MuiSlider-markLabel[data-index="0"]': {
              transform: 'translateX(0)',
            },
            '& .MuiSlider-thumb': { width: 12, height: 12 },
          }}
        />
      </Stack>
      <Tooltip title="Recompute correlations + causality" arrow>
        <IconButton
          size="small"
          onClick={load}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <DetailDrawer
        focus={focus}
        onClose={() => setFocus(null)}
        onFocusPair={(a, b) => setFocus({ type: 'pair', a, b })}
        corrEdges={corrEdges}
        causeEdges={causeEdges}
        bundles={display.bundles}
        families={families}
        theme={theme}
        nameOf={nameOf}
      />
    </Card>
  );
};

export default SignalRelationshipsGraph;
